/**
 * ACDL (Adobe Client Data Layer) utilities for mi-chat-renderer
 * Implements tracking requirements from Conversational AI Search Tracking Requirements
 * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
 */

export type DataLayerType = Record<string, unknown>;

export interface ACDLEvent {
  event: string;
  metadata?: ACDLMetadata;
  page?: ACDLPage;
  user?: ACDLUser;
  [key: string]: unknown;
}

export interface ACDLMetadata {
  allow_marketing?: boolean;
  site_id?: string;
  language_domain?: string;
  platform?: string;
  server_id?: string;
  session_id?: string;
  latitude?: string;
  longitude?: string;
  geo_country?: string;
  geo_state?: string;
  geo_city?: string;
}

export interface ACDLPage {
  name: string;
  url?: string;
  modal?: boolean;
  template?: string;
  section?: string;
  experience?: string;
}

export interface ACDLUser {
  alt_customer_id?: string;
  authentication_status?: string;
  member_level?: string;
  member_city?: string;
  member_state?: string;
  member_country?: string;
  upcoming_stay?: string;
  stay_in_progress?: string;
  cc_all?: string;
  nights_booked_this_year?: string;
  points_balance?: string;
  member_join_date?: string;
  account_age?: string;
}

/**
 * ACDL Search object for SEARCH events
 * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/2345959449
 */
export interface ACDLSearch {
  /** Numeric count of properties returned. If 0 then set 0. */
  number_of_search_results: number;
  /** Type of search - "Conversational AI Search" for AI chat */
  type: string;
}

/**
 * ACDL Product object for SEARCH events
 * Represents a property/hotel in the search results
 */
export interface ACDLProduct {
  /** MARSHA code or Property ID */
  id: string;
  /** Category - "ROOM" for hotels */
  category: string;
  /** Subcategory - "ROOM" for hotels */
  subcategory: string;
  /** MARSHA code or Property ID */
  property_id: string;
  /** Average nightly cost per room/unit, excluding taxes and fees */
  cost?: number;
}

/**
 * Extended ACDL Event interface for SEARCH events
 */
export interface ACDLSearchEvent extends ACDLEvent {
  search: ACDLSearch;
  products?: ACDLProduct[];
}

/**
 * AI Search page name constants as per tracking requirements
 */
export const AI_SEARCH_PAGE_NAMES = {
  JOIN_OR_LOGIN: 'AI Search|Join or Login',
  WELCOME: 'AI Search|Welcome',
  SEARCH_RESULTS: 'AI Search|Search Results',
  SEARCH_DEFAULT: 'AI Search|Search Default',
  SEARCH_REDIRECT_BLOCK: 'AI Search|Search Redirect Block',
  SEARCH_NO_RESULTS: 'AI Search|Search No Results',
  SYSTEM_ERROR: 'AI Search|System Error',
  PROPERTY_DETAILS: 'AI Search|Property Details',
  GLOBAL_LOGIN: 'Global Login',
} as const;

/**
 * AI Search template types from UXL uiType
 */
export const AI_SEARCH_TEMPLATES = {
  INTEGRATED_IMAGERY: 'AI Search|integratedImagery',
  CATEGORY_LIST: 'AI Search|categoryList',
  DEFAULT: 'AI Search|default',
  REDIRECT_BLOCK: 'AI Search|redirectBlock',
  NO_RESULTS: 'AI Search|No Results',
  SYSTEM_ERROR: 'AI Search|System Error',
} as const;

/**
 * ACDL Event names
 */
export const ACDL_EVENT_NAMES = {
  PAGEVIEW: 'PAGEVIEW',
  LOGIN: 'LOGIN',
  SEARCH: 'SEARCH',
} as const;

/**
 * Click tracking location for AI Search
 * Used in custom_click_track_value attribute
 */
export const AI_SEARCH_CLICK_TRACK_LOC = 'AI Search';

/**
 * Click tracking values for AI Search interactions
 * Format: <location>|<action>|internal|<event>
 */
export const AI_SEARCH_CLICK_TRACK = {
  /** When user submits a search query via enter or arrow button */
  SEARCH_WITH_AI: `${AI_SEARCH_CLICK_TRACK_LOC}|Search with AI|internal|event207`,
  /** When user clicks on a suggested search pill - format: "AI Search|Pill {index} - {text}|internal|event207" */
  SUGGESTED_SEARCH_PILL: (chipText: string, index: number) =>
    `${AI_SEARCH_CLICK_TRACK_LOC}|Pill ${index + 1} - ${chipText}|internal|event207`,
  /** When user clicks on "View all X properties" link to expand search results */
  VIEW_ALL_PROPERTIES: `${AI_SEARCH_CLICK_TRACK_LOC}|View All Properties|internal`,
  /** When user clicks on "View Rates" / "View Property" CTA on a property card */
  VIEW_RATES: `${AI_SEARCH_CLICK_TRACK_LOC}|AI Search Card View Rates|internal|event415`,
  /** When user clicks the thumbs up / like button on search results */
  LIKE_RESULTS: `${AI_SEARCH_CLICK_TRACK_LOC}|Like Results|internal|event800`,
  /** When user clicks the thumbs down / dislike button on search results */
  DISLIKE_RESULTS: `${AI_SEARCH_CLICK_TRACK_LOC}|Dislike Results|internal|event801`,
  /** When user clicks the favorite/heart button on a property card */
  FAVORITE_HOTEL: `${AI_SEARCH_CLICK_TRACK_LOC}|Favorite Hotel|internal|event23`,
} as const;

/**
 * Helper to safely get a value with fallback
 */
export const ternaryHandler = (value: unknown, defaultValue: string | boolean | unknown = ''): unknown => {
  return value ?? defaultValue;
};

/**
 * Recursively sanitizes an object by removing keys with null, undefined, or empty string values
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): Partial<T> {
  if (typeof obj !== 'object' || obj === null) return obj;

  const cleanedObj: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value === '' || value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = sanitizeObject(value as Record<string, unknown>);
      if (Object.keys(nested ?? {}).length > 0) {
        cleanedObj[key] = nested;
      }
    } else {
      cleanedObj[key] = value;
    }
  }

  return cleanedObj as Partial<T>;
}

/**
 * Calculates account age in days from join date
 */
export function accountAgeCalculator(datalayer: DataLayerType): string {
  if (datalayer?.['memState'] === 'authenticated') {
    const joinDate = (ternaryHandler(datalayer?.['mr_prof_join_date']) as string).replaceAll('-', '/');
    const joinDateObj = new Date(joinDate);
    const today = new Date();

    const diffInMs: number = today.getTime() - joinDateObj.getTime();
    const diffInDays = Math.round(diffInMs / 86400000);

    if (!isNaN(diffInDays)) {
      return diffInDays.toString();
    }
    return '';
  }
  return '';
}

/**
 * Formats date to MM/DD/YYYY format
 */
export const updateDateFormat = (input: string, format = 'MM/DD/YYYY'): string => {
  if (input === '') return '';

  const date = new Date(input);
  if (isNaN(date.getTime())) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  if (format === 'MM/DD/YYYY') {
    return `${month}/${day}/${year}`;
  }
  return input;
};

/**
 * Generates ACDL keys for PAGEVIEW events
 * Based on MARRIOTT.COM PAGEVIEW Technical Requirements
 */
export const pageViewACDLkeyGenerator = ({
  datalayer,
  pageName,
  isModal = false,
  pageTemplate,
  pageUrl,
}: {
  datalayer?: DataLayerType;
  pageName: string;
  isModal?: boolean;
  pageTemplate?: string;
  pageUrl?: string;
}): ACDLEvent => {
  // Build full URL including query string and hash
  const getFullUrl = (): string => {
    if (typeof window === 'undefined') return '';
    const { host, pathname, search, hash } = window.location;
    return `${host}${pathname}${search}${hash}`;
  };

  const payload: ACDLEvent = {
    event: ACDL_EVENT_NAMES.PAGEVIEW,
    metadata: {
      allow_marketing: true,
      site_id: ternaryHandler(datalayer?.['env_site_id']) as string,
      language_domain: ternaryHandler(datalayer?.['env_site_id']) as string,
      platform: ternaryHandler(datalayer?.['env_platform']) as string,
      server_id: ternaryHandler(datalayer?.['env_server_id']) as string,
      session_id: ternaryHandler(datalayer?.['sessionId']) as string,
      latitude: ternaryHandler(datalayer?.['browser_akamai_loc_lat']) as string,
      longitude: ternaryHandler(datalayer?.['browser_akamai_loc_long']) as string,
      geo_country: ternaryHandler(datalayer?.['browser_akamai_loc_country']) as string,
      geo_state: ternaryHandler(datalayer?.['browser_akamai_loc_state']) as string,
      geo_city: ternaryHandler(datalayer?.['browser_akamai_loc_city']) as string,
    },
    page: {
      name: pageName,
      url: pageUrl || getFullUrl(),
      modal: isModal,
      ...(pageTemplate && { template: pageTemplate }),
    },
    user: {
      alt_customer_id: ternaryHandler(
        datalayer?.['altCustId'],
        ternaryHandler(datalayer?.['mr_id_alternate'])
      ) as string,
      authentication_status: ternaryHandler(datalayer?.['memState']) as string,
      member_level: ternaryHandler(datalayer?.['mr_prof_member_program_level']) as string,
      member_city: ternaryHandler(datalayer?.['mr_prof_address_city']) as string,
      member_state: ternaryHandler(datalayer?.['mr_prof_address_state']) as string,
      member_country: ternaryHandler(datalayer?.['mr_prof_address_country']) as string,
      upcoming_stay: ternaryHandler(datalayer?.['mr_prof_upcoming_stay']) as string,
      stay_in_progress: ternaryHandler(datalayer?.['mr_prof_stay_in_progress']) as string,
      cc_all: ternaryHandler(datalayer?.['mr_prof_cc_all']) as string,
      nights_booked_this_year: ternaryHandler(datalayer?.['mr_prof_nights_booked_this_year']) as string,
      points_balance: ternaryHandler(datalayer?.['mr_prof_points_balance']) as string,
      member_join_date: updateDateFormat(ternaryHandler(datalayer?.['mr_prof_join_date']) as string),
      account_age: accountAgeCalculator(datalayer ?? {}),
    },
  };

  return sanitizeObject(payload) as ACDLEvent;
};

/**
 * Search type constant for Conversational AI Search
 */
export const AI_SEARCH_TYPE = 'Conversational AI Search';

/**
 * Property data interface for search results tracking
 */
export interface SearchResultProperty {
  /** MARSHA code or Property ID */
  propertyId: string;
  /** Average nightly cost (optional) */
  cost?: number;
}

/**
 * Generates ACDL keys for SEARCH events
 * Based on MARRIOTT.COM Conversational AI SEARCH Technical Requirements
 * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/2345959449
 *
 * @example
 * const searchEvent = searchACDLkeyGenerator({
 *   datalayer,
 *   numberOfResults: 5,
 *   pageTemplate: AI_SEARCH_TEMPLATES.INTEGRATED_IMAGERY,
 *   properties: [
 *     { propertyId: 'NYCMQ', cost: 250.00 },
 *     { propertyId: 'MCOWS', cost: 199.00 },
 *   ],
 * });
 */
export const searchACDLkeyGenerator = ({
  datalayer,
  numberOfResults,
  pageTemplate,
  properties = [],
}: {
  datalayer?: DataLayerType;
  /** Number of properties returned in search results */
  numberOfResults: number;
  /** Page template (e.g., AI_SEARCH_TEMPLATES.INTEGRATED_IMAGERY) */
  pageTemplate?: string;
  /** Array of properties returned in search results */
  properties?: SearchResultProperty[];
}): ACDLSearchEvent => {
  // Build products array from properties
  const products: ACDLProduct[] = properties.map(prop => ({
    category: 'ROOM',
    subcategory: 'ROOM',
    id: prop.propertyId,
    property_id: prop.propertyId,
    ...(prop.cost !== undefined && { cost: prop.cost }),
  }));

  const payload: ACDLSearchEvent = {
    event: ACDL_EVENT_NAMES.SEARCH,
    user: {
      alt_customer_id: ternaryHandler(
        datalayer?.['altCustId'],
        ternaryHandler(datalayer?.['mr_id_alternate'])
      ) as string,
      authentication_status: ternaryHandler(datalayer?.['memState']) as string,
      member_level: ternaryHandler(datalayer?.['mr_prof_member_program_level']) as string,
      member_city: ternaryHandler(datalayer?.['mr_prof_address_city']) as string,
      member_state: ternaryHandler(datalayer?.['mr_prof_address_state']) as string,
      member_country: ternaryHandler(datalayer?.['mr_prof_address_country']) as string,
      upcoming_stay: ternaryHandler(datalayer?.['mr_prof_upcoming_stay']) as string,
      stay_in_progress: ternaryHandler(datalayer?.['mr_prof_stay_in_progress']) as string,
      cc_all: ternaryHandler(datalayer?.['mr_prof_cc_all']) as string,
      nights_booked_this_year: ternaryHandler(datalayer?.['mr_prof_nights_booked_this_year']) as string,
      points_balance: ternaryHandler(datalayer?.['mr_prof_points_balance']) as string,
      member_join_date: updateDateFormat(ternaryHandler(datalayer?.['mr_prof_join_date']) as string),
      account_age: accountAgeCalculator(datalayer ?? {}),
    },
    page: {
      name: AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS,
      ...(pageTemplate && { template: pageTemplate }),
    },
    search: {
      number_of_search_results: numberOfResults,
      type: AI_SEARCH_TYPE,
    },
    ...(products.length > 0 && { products }),
  };

  return sanitizeObject(payload) as ACDLSearchEvent;
};

/**
 * UXL `uiType` values that represent an AI Search result response.
 *
 * A SEARCH event and Search Results PAGEVIEW should fire for each of these,
 * regardless of whether properties were returned (e.g. `noResults`, `systemError`).
 * Kept in sync with `getSearchResultsPageName` / `getSearchResultsTemplate`.
 */
export const SEARCH_RESULT_UI_TYPES = new Set<string>([
  'integratedImagery',
  'categoryList',
  'default',
  'redirectBlock',
  'noResults',
  'systemError',
]);

/**
 * Callbacks interface for trackSearchAnalytics function
 */
export interface TrackSearchAnalyticsCallbacks {
  trackSearchResultsPageView: (uiType?: string) => void;
  trackSearchEvent: (data: { numberOfResults: number; properties: SearchResultProperty[] }) => void;
}

/**
 * Pending preview data structure for analytics tracking
 */
export interface PendingPreviewForAnalytics {
  viewData?: {
    rawData?: {
      data?: {
        search?: {
          conversationalSearch?: {
            searchByNaturalLanguageQuery?: {
              edges?: Array<{
                node?: {
                  property?: { id?: string };
                  rates?: Array<{
                    rateModes?: {
                      lowestAverageRate?: {
                        amountPlusMandatoryFees?: { amount?: number };
                        amount?: { amount?: number };
                      };
                    };
                  }>;
                };
              }>;
            };
          };
        };
      };
    };
  };
  itemCount?: number;
}

/**
 * Tracks search analytics for AI search results.
 * Calls the provided tracking callbacks with formatted data from the pending preview.
 *
 * @param callbacks - Object containing trackSearchResultsPageView and trackSearchEvent functions
 * @param uiType - The UI type from the response (e.g., 'integratedImagery', 'categoryList')
 * @param pending - The pending preview data containing search results
 */
export function trackSearchAnalytics(
  callbacks: TrackSearchAnalyticsCallbacks,
  uiType: string | null,
  pending: PendingPreviewForAnalytics | null | undefined
): void {
  callbacks.trackSearchResultsPageView(uiType ?? undefined);

  const searchEdges =
    pending?.viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges ?? [];

  const searchProperties: SearchResultProperty[] = Array.isArray(searchEdges)
    ? searchEdges.map(edge => {
        const propertyId = edge?.node?.property?.id ?? '';
        const rateNode = edge?.node?.rates?.[0];
        const lowestAverageRate = rateNode?.rateModes?.lowestAverageRate;
        const cost =
          lowestAverageRate?.amountPlusMandatoryFees?.amount ?? lowestAverageRate?.amount?.amount ?? undefined;
        return { propertyId, cost };
      })
    : [];

  callbacks.trackSearchEvent({
    numberOfResults: pending?.itemCount ?? 0,
    properties: searchProperties.filter(p => p.propertyId),
  });
}
